import { patientsQueryOptions } from "../api/queries";
import { useQuery } from "@tanstack/react-query";

// import { getQueryClient } from "~/providers/query.provider";

import { useState, useCallback, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@workspace/web-ui/components/button";
import { Patient } from "../api/types";
import PatientDetails from "../components/patient-details";
import { PatientCard } from "../components/card/patient-card/patient-card";
import { PatientLogFilters } from "../components/patient-log-filters";
import { FilterOption } from "../types";
// import { patientLogsKeys } from "../api/queries/keys";
import { useDebounce } from "~/hooks/use-debounce";
// import DosespotModal from "./dosespotModal";
import { getRouteApi } from "@tanstack/react-router";
import { getApiProgram } from "~/lib/utils";
import UrgentCareSessionDetails from "../components/urgent-care-session-details";
import { useActiveSessionStore } from "../store/active-session-store";

export const filterOptions: FilterOption[] = [
    {
        label: "All",
        value: "all"
    },
    {
        label: "Pending Review",
        value: "pending_review"
    },
    {
        label: "Treatment Completed",
        value: "treatment_completed"
    },
];

export const paymentFilterOptions: FilterOption[] = [
    {
        label: "All",
        value: "all"
    },
    {
        label: "Paid",
        value: "paid"
    },
    {
        label: "Not Paid",
        value: "not_paid"
    },
];

export const verificationFilterOptions: FilterOption[] = [
    {
        label: "All",
        value: "all"
    },
    {
        label: "Verified",
        value: "verified"
    },
    {
        label: "Unverified",
        value: "unverified"
    },
];

export function PatientsPage() {
    // const queryClient = getQueryClient()
    const [filterValue, setFilterValue] = useState(filterOptions?.[0]?.value ?? '');
    const [vericationValue, setVerificationValue] = useState(verificationFilterOptions?.[0]?.value ?? '')
    const [paymentFilterValue, setPaymentFilterValue] = useState(paymentFilterOptions?.[0]?.value ?? '');
    const [searchValue, setSearchValue] = useState<string>("");
    const debouncedSearchValue = useDebounce<string>(searchValue, 400);
    const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
    const [isPatientDetailsOpen, setIsPatientDetailsOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;
    const skip = (currentPage - 1) * pageSize;
    const routeApi = getRouteApi("/(dashboard)/dashboard/$program");
    const { program } = routeApi.useParams();
    const apiProgram = getApiProgram(program)
    const { isOpen, close } = useActiveSessionStore()

    const { data, isPending,  } = useQuery(patientsQueryOptions({
        limit: pageSize,
        skip,
        search: debouncedSearchValue,
        // status: filterValue,
        status_filter: filterValue,
        verification_status: vericationValue,
        payment_status: paymentFilterValue,
        program: apiProgram
    }));

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchValue, filterValue]);

    // Prefetch the next page!
    // useEffect(() => {
    //     if (!isPlaceholderData && data?.hasMore) {
    //         queryClient.prefetchQuery({
    //             queryKey: patientLogsKeys.list({
    //                 limit: pageSize,
    //                 skip: skip + pageSize,
    //                 search: debouncedSearchValue,
    //                 status: filterValue,
    //             }),
    //             queryFn: () => patientsQueryOptions({
    //                 limit: pageSize,
    //                 skip: skip + pageSize,
    //                 search: debouncedSearchValue,
    //                 status: filterValue,
    //             }),
    //         })
    //     }
    // }, [data, isPlaceholderData, debouncedSearchValue, filterValue, pageSize, skip, queryClient])


    // Pagination calculations
    const totalPages = Math.ceil((data?.total ?? 0) / pageSize);
    const startIndex = (skip) + 1;
    const endIndex = Math.min(startIndex + (data?.patients?.length ?? 0) - 1, (data?.total ?? 0));

    const handlePatientSelect = useCallback((patient: Patient | null) => {
        setSelectedPatient(patient);
        close()
        // On mobile, open patient details as a modal or slide-in panel
        if (window.innerWidth < 1024) {
            setIsPatientDetailsOpen(!!patient);
        }
    }, []);

    const closePatientDetails = () => {
        setSelectedPatient(null);
        setIsPatientDetailsOpen(false);
    };

    const handlePageChange = (page: number) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    // Clear selected patient when page changes
    useEffect(() => {
        setSelectedPatient(null);
        setIsPatientDetailsOpen(false);
    }, [currentPage]);

    return (
        <div className="w-full max-w-[1440px] mx-auto flex flex-col px-4 sm:px-6 md:px-8 lg:px-12 gap-4 sm:gap-6 py-4 sm:py-6 hide-scrollbar">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-gray-800">
                Patient Management
            </h2>

            <PatientLogFilters
                searchValue={searchValue}
                onSearchChange={setSearchValue}
                filterValue={program === 'urgent-care' ? filterValue : paymentFilterValue}
                onFilterChange={program === 'urgent-care' ? setFilterValue : setPaymentFilterValue}
                filterOptions={program === 'urgent-care' ? filterOptions : paymentFilterOptions}
                verificationValue={vericationValue}
                onVerificationChange={setVerificationValue}
                verificationOptions={verificationFilterOptions}
            />

            {data?.patients?.length === 0 ? (
                <div className="flex flex-col items-center w-full min-h-[50vh] py-16">
                    <h2 className="text-lg font-medium">No patients found</h2>
                    <p className="text-sm text-gray-500">When patients are available, they will appear here</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-[40%_60%] gap-4 sm:gap-6 md:gap-8 w-full">
                    {/* Patient List Section */}
                    <div className="w-full flex flex-col">
                        <div className="lg:max-h-[calc(100vh-16rem)] lg:overflow-y-auto hide-scrollbar lg:pr-2">
                            {isPending ? (
                                <div className="flex flex-col w-full space-y-3 sm:space-y-4 border">
                                    {[...Array(3)].map((_, index) => (
                                        <div
                                            key={index}
                                            className="w-full p-4 border-b last:border-b-0 animate-pulse"
                                        >
                                            <div className="flex items-center space-x-4">
                                                <div className="h-12 w-12 bg-white rounded-full" />
                                                <div className="space-y-2 flex-1">
                                                    <div className="h-4 bg-white rounded w-1/4" />
                                                    <div className="h-3 bg-white rounded w-1/3" />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="space-y-3 sm:space-y-4 w-full">
                                    {data?.patients?.map((patient) => (
                                        <PatientCard
                                            key={patient.userId}
                                            patient={patient}
                                            selected={selectedPatient}
                                            onSelect={handlePatientSelect}
                                            className="transition-all duration-200 hover:shadow-sm"
                                            program={program}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Pagination UI - Outside scroll area */}
                        <div className="flex items-center text-xs sm:text-base justify-between border-t border-gray-200 px-2 py-3 mt-4">
                            <div className="text-gray-700">
                                Showing <span className="font-medium">{startIndex}</span> to{" "}
                                <span className="font-medium">{endIndex}</span> of{" "}
                                <span className="font-medium">{(data?.total ?? 0)}</span> Patients
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="icon"
                                    disabled={currentPage === 1 || isPending}
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    className="h-8 w-8 rounded cursor-pointer"
                                    aria-label="Previous page"
                                >
                                    <ChevronLeft className="h-4 w-4 cursor-pointer" />
                                </Button>
                                <span className="font-medium">
                                    Page {currentPage} of {totalPages}
                                </span>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    disabled={currentPage === totalPages || isPending}
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    className="h-8 w-8 rounded cursor-pointer"
                                    aria-label="Next page"
                                >
                                    <ChevronRight className="h-4 w-4 cursor-pointer" />
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* Patient Details Section - Desktop */}
                    <div className="hidden lg:block w-full sticky top-4 lg:max-h-[calc(100vh-12rem)] lg:overflow-y-auto hide-scrollbar">
                        {isPending ? (
                            <div className="animate-pulse p-4 space-y-4">
                                <div className="h-8 bg-white rounded w-3/4"></div>
                                <div className="space-y-2">
                                    <div className="h-4 bg-white rounded"></div>
                                    <div className="h-4 bg-white rounded w-5/6"></div>
                                    <div className="h-4 bg-white rounded w-4/6"></div>
                                </div>
                                <div className="space-y-2">
                                    <div className="h-4 bg-white rounded"></div>
                                    <div className="h-4 bg-white rounded w-5/6"></div>
                                    <div className="h-4 bg-white rounded w-4/6"></div>
                                </div>
                            </div>
                        ) : !selectedPatient ? (
                            <div className="flex justify-center h-full rounded-lg">
                                <p className="text-base font-medium text-gray-500">
                                    Click an item to see details
                                </p>
                            </div>
                        ) : (
                            isOpen ? (
                                <UrgentCareSessionDetails patient={selectedPatient} />
                            ) : (
                                <PatientDetails
                                    patient={selectedPatient}
                                />
                            )
                        )}
                    </div>

                    {/* Patient Details Modal - Mobile */}
                    {isPatientDetailsOpen && (
                        <div
                            className="fixed inset-0 z-50 lg:hidden overflow-y-auto hide-scrollbar bg-white"
                            role="dialog"
                            aria-modal="true"
                        >
                            <div className="relative w-full h-full">
                                {/* Close Button */}
                                <button
                                    onClick={closePatientDetails}
                                    className="absolute h-6 w-6 top-4 flex items-center justify-center right-4 z-60 p-2 bg-gray-100 rounded-full"
                                    aria-label="Close patient details"
                                >
                                    ✕
                                </button>

                                {/* Patient Details Content */}
                                <div className="pt-16 px-4">
                                    {selectedPatient && (
                                        isOpen ? (
                                            <UrgentCareSessionDetails patient={selectedPatient} />
                                        ) : (
                                            <PatientDetails
                                                patient={selectedPatient}
                                            />
                                        )
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}